import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import API_BASE_URL from "../config/apiConfig";
import "./QuizZonePage.css";

const SITE_URL = "https://www.aamschool.in";

const tryFetchTopics = async (subject) => {
  const encodedSubject = encodeURIComponent(subject);
  const urls = [
    `${API_BASE_URL}/questions/${encodedSubject}/topics`,
    `${API_BASE_URL}/questions/topics?subject=${encodedSubject}`,
    `${API_BASE_URL}/questions//topics?subject=${encodedSubject}`,
  ];

  let lastError = null;
  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastError = new Error(`Topics API failed: ${response.status}`);
        continue;
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Failed to load topics.");
};

function QuizZonePage() {
  const location = useLocation();
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [topics, setTopics] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadSubjects = async () => {
      setLoadingSubjects(true);
      setError("");
      try {
        const response = await fetch(`${API_BASE_URL}/questions/subjects`);
        if (!response.ok) throw new Error("Failed to load subjects.");
        const data = await response.json();
        const mergedSubjects = Array.isArray(data) ? data.filter(Boolean) : [];
        if (!mounted) return;
        setSubjects(mergedSubjects);
        setSelectedSubject((prev) => prev || mergedSubjects[0] || "");
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Failed to load subjects.");
      } finally {
        if (mounted) setLoadingSubjects(false);
      }
    };
    loadSubjects();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const requestedSubject = String(location.state?.selectedSubject || "").trim();
    if (!requestedSubject || !subjects.length) return;
    if (!subjects.includes(requestedSubject)) return;
    setSelectedSubject(requestedSubject);
  }, [location.state, subjects]);

  useEffect(() => {
    if (!selectedSubject) {
      setTopics([]);
      return;
    }

    let mounted = true;
    const loadTopics = async () => {
      setLoadingTopics(true);
      setError("");
      try {
        const data = await tryFetchTopics(selectedSubject);
        if (!mounted) return;
        setTopics(data);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Failed to load topics.");
        setTopics([]);
      } finally {
        if (mounted) setLoadingTopics(false);
      }
    };
    loadTopics();
    return () => {
      mounted = false;
    };
  }, [selectedSubject]);

  const canonicalUrl = `${SITE_URL}/quiz-zone`;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Quiz Zone",
    url: canonicalUrl,
    description:
      "Explore free subject-wise and topic-wise quiz practice on AAMSchool to improve preparation, revision, and exam confidence.",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Quiz Zone",
          item: canonicalUrl,
        },
      ],
    },
  };

  return (
    <div className="quiz-zone-page">
      <Helmet>
        <title>Free Quiz Zone | AAMSchool</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="description"
          content="Explore free subject-wise and topic-wise quiz practice on AAMSchool to improve preparation, revision, and exam confidence."
        />
        <meta property="og:title" content="Free Quiz Zone | AAMSchool" />
        <meta
          property="og:description"
          content="Explore free subject-wise and topic-wise quiz practice on AAMSchool to improve preparation, revision, and exam confidence."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <div className="quiz-zone-hero">
        <h1>Quiz Zone</h1>
        <p>Select a subject and start topic-wise practice.</p>
      </div>

      <div className="quiz-subject-strip-wrap">
        {loadingSubjects ? <p>Loading subjects...</p> : null}
        {!loadingSubjects && subjects.length === 0 ? <p>No subjects available.</p> : null}
        <div className="quiz-subject-strip">
          {subjects.map((subject) => (
            <button
              type="button"
              key={subject}
              className={`quiz-subject-chip ${selectedSubject === subject ? "active" : ""}`}
              onClick={() => setSelectedSubject(subject)}
            >
              {subject}
            </button>
          ))}
        </div>
      </div>

      {error ? <p className="quiz-zone-error">{error}</p> : null}

      <div className="quiz-topic-grid">
        {loadingTopics ? <p>Loading topics...</p> : null}
        {!loadingTopics && topics.length === 0 ? (
          <p>No topics found for this subject.</p>
        ) : null}
        {!loadingTopics &&
          topics.map((topicItem, idx) => (
            <Link
              className="quiz-topic-card"
              key={`${topicItem?.topic || "topic"}-${idx}`}
              to={
                topicItem?.path ||
                `/quiz-zone/${encodeURIComponent(selectedSubject)}/${encodeURIComponent(
                  String(topicItem?.topic || "")
                )}/quizzes`
              }
              state={{ selectedSubject }}
              aria-label={`Open ${topicItem?.topic || "topic"} quizzes`}
            >
              <h3>{topicItem?.topic || "Untitled Topic"}</h3>
              <div className="quiz-topic-meta">
                <span>Available Quizzes</span>
                <strong>{topicItem?.quizCount ?? 0}</strong>
              </div>
            </Link>
          ))}
      </div>
    </div>
  );
}

export default QuizZonePage;
